import { VARIABLE_REGEX } from "./regex";
import { BUILTIN_PROVIDERS } from "../providers/builtinProviders";

/**
 * creates a variable scope resolver.
 *
 * scope priority (highest → lowest):
 *   local  →  environment  →  global  →  dynamic ($)
 *
 * @param {Object} globalVars      - map of global variable names to values
 * @param {Object} environmentVars - map of active environment variable names to values
 * @param {Object} localVars       - map of endpoint-local variable names to values
 * @param {Object} providerRegistry - map of dynamic provider names to generator fns
 *                                    defaults to all built-in providers.
 */
export const createScopeResolver = (
  globalVars = {},
  environmentVars = {},
  localVars = {},
  providerRegistry = BUILTIN_PROVIDERS,
) => {
  const resolveVariable = (name, args = []) => {
    if (name.startsWith("$")) {
      const cleanName = name.slice(1);
      const provider = providerRegistry[cleanName];
      if (!provider) return { value: undefined, source: "unknown" };
      try {
        return { value: provider(...args), source: "dynamic" };
      } catch {
        return { value: undefined, source: "unknown" };
      }
    }

    if (Object.prototype.hasOwnProperty.call(localVars, name)) {
      return { value: localVars[name], source: "local" };
    }
    if (Object.prototype.hasOwnProperty.call(environmentVars, name)) {
      return { value: environmentVars[name], source: "environment" };
    }
    if (Object.prototype.hasOwnProperty.call(globalVars, name)) {
      return { value: globalVars[name], source: "global" };
    }

    return { value: undefined, source: "unknown" };
  };

  return { resolveVariable };
};

export const asMap = (arr) => {
  if (!Array.isArray(arr)) return {};
  return Object.fromEntries(arr.map((v) => [v.variable, v.value]));
};

export { VARIABLE_REGEX };
